import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  ChartLineUp, Users, Receipt, ArrowsClockwise, Wallet, ScrollText,
  SignOut, ShieldCheck, Scroll, FileText, DownloadSimple, CurrencyDollar, GraduationCap, Storefront,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/", label: "Dashboard", icon: ChartLineUp, testid: "nav-dashboard" },
  { to: "/reps", label: "Reps", icon: Users, testid: "nav-reps" },
  { to: "/ledger", label: "Commission Ledger", icon: Receipt, testid: "nav-ledger" },
  { to: "/reconciliation", label: "Reconciliation", icon: ArrowsClockwise, testid: "nav-reconciliation" },
  { to: "/votel", label: "Votel Sales", icon: Storefront, testid: "nav-votel" },
  { to: "/payouts", label: "Payout Run", icon: Wallet, testid: "nav-payouts" },
  { to: "/contract-template", label: "Contract Template", icon: FileText, testid: "nav-contract-template" },
  { to: "/tax", label: "Tax / 1099", icon: CurrencyDollar, testid: "nav-tax" },
  { to: "/training", label: "Training", icon: GraduationCap, testid: "nav-training" },
  { to: "/audit", label: "Audit Trail", icon: Scroll, testid: "nav-audit" },
];

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [downloading, setDownloading] = useState(false);

  const doLogout = async () => { await logout(); navigate("/login"); };

  const downloadSource = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/admin/download-source", { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename=([^;]+)/);
      const fname = match ? match[1].trim() : "partner-command-center.zip";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url; a.download = fname; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
      toast.success("App source downloaded");
    } catch (e) {
      toast.error("Download failed — try again");
    } finally { setDownloading(false); }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="fixed inset-y-0 left-0 z-30 flex w-60 flex-col border-r border-border bg-card">
        <div className="flex items-center gap-2.5 border-b border-border px-5 py-5">
          <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-primary text-primary-foreground">
            <ShieldCheck size={18} weight="fill" />
          </div>
          <div className="leading-tight">
            <div className="font-heading text-sm font-extrabold tracking-tight">Command Center</div>
            <div className="overline text-[9px]">Partner Payouts</div>
          </div>
        </div>
        <nav className="flex-1 space-y-0.5 px-3 py-4">
          {NAV.map((item) => {
            const active = item.to === "/" ? location.pathname === "/" : location.pathname.startsWith(item.to);
            const Icon = item.icon;
            return (
              <Link key={item.to} to={item.to} data-testid={item.testid}
                className={cn(
                  "flex items-center gap-3 rounded-sm px-3 py-2 text-sm font-medium transition-colors duration-200",
                  active ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-accent hover:text-foreground"
                )}>
                <Icon size={18} weight={active ? "fill" : "regular"} />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="border-t border-border p-3">
          <div className="mb-2 px-2">
            <div className="text-sm font-semibold">{user?.name || "Admin"}</div>
            <div className="truncate font-mono text-[11px] text-muted-foreground">{user?.email}</div>
          </div>
          <Button data-testid="download-source-btn" variant="outline" size="sm"
            className="mb-1.5 w-full justify-start gap-2" onClick={downloadSource} disabled={downloading}>
            <DownloadSimple size={16} /> {downloading ? "Packaging…" : "Download App (ZIP)"}
          </Button>
          <Button data-testid="logout-btn" variant="ghost" size="sm"
            className="w-full justify-start gap-2 text-muted-foreground hover:text-foreground" onClick={doLogout}>
            <SignOut size={16} /> Sign out
          </Button>
        </div>
      </aside>
      <main className="ml-60 flex-1">
        <div className="mx-auto max-w-[1400px] px-8 py-8">{children}</div>
      </main>
    </div>
  );
}
